<script setup lang="ts">
import CountryFlag from 'vue-country-flag-next'
import { CurrencyDisplay, useCurrencyInput } from 'vue-currency-input'
import { computed, onMounted, toValue, watch } from 'vue'
import { useCurrency } from '@/composables/useCurrency'
import { CurrencyValue } from '@/modules/expert/custom-fields/types'

const emit = defineEmits(['currency', 'blur'])

const props = defineProps<{
  value?: unknown
  precision?: number
  error?: string | boolean
  min?: number
  labelField?: string | undefined
  valueField?: string | undefined
  label?: string
  description?: string | null
  tooltip?: string | null
  required?: boolean
}>()

const { selectedCurrency, currencyList, placeholder } = useCurrency()

const value = computed({
  get: () => props.value,
  set: (newVal: unknown) => {
    if ((newVal as CurrencyValue)?.amount === null)
      return emit('currency', null)
    const t = {
      currency: (newVal as CurrencyValue)?.currency,
      amount:
        (newVal as CurrencyValue)?.amount == '0'
          ? '0.00'
          : String(Number((newVal as CurrencyValue)?.amount)?.toFixed(2)),
    }
    emit('currency', t)
  },
})

const { inputRef, numberValue, setOptions, setValue, formattedValue } =
  useCurrencyInput({
    currency: 'BRL',
    useGrouping: false,
    autoDecimalDigits: true,
    precision: props.precision || 2,
    hideCurrencySymbolOnFocus: false,
    valueRange: {
      min: props.min || undefined,
    },
  })

watch(selectedCurrency, () => {
  setOptions({
    currency: selectedCurrency.value.currencyCode || 'BRL',
    currencyDisplay: 'symbol' as CurrencyDisplay,
    precision: props.precision || 2,
    useGrouping: false,

    autoDecimalDigits: true,
    valueRange: {
      min: props.min || undefined,
    },
  })
  value.value = {
    currency: selectedCurrency.value.currencyCode,
    amount: (value.value as CurrencyValue)?.amount,
  }
})

// reseta caso o valor esteja fora do permitido
watch(
  () => props.min,
  () => {
    if (
      typeof props.min != 'undefined' &&
      numberValue.value &&
      numberValue.value < props.min
    )
      setValue(props.min)
  }
)

// a verificação existe para não setar valor nulo
watch(numberValue, (newVal) => {
  value.value = {
    currency: selectedCurrency.value.currencyCode,
    amount: newVal,
  }
})

onMounted(() => {
  // esse timeout é necessário para que a ref interna da lib seja carregada para depois ser inicializada
  setTimeout(() => {
    if ((props.value as CurrencyValue)?.currency)
      selectedCurrency.value =
        currencyList.find((c) =>
          ((props.value as CurrencyValue)?.currency as string)
            .toLowerCase()
            .includes(c.isoCode.toLowerCase())
        ) || currencyList[0]
    if (
      (props.value as CurrencyValue)?.amount != String(numberValue.value) &&
      typeof (props.value as CurrencyValue)?.amount != 'undefined'
    ) {
      setValue(Number((props.value as CurrencyValue)?.amount))
    }
  }, 500)
})
</script>

<template>
  <div class="d-flex flex-row">
    <v-select
      v-model="selectedCurrency"
      variant="plain"
      :items="currencyList"
      :item-title="String(props.labelField)"
      :item-value="String(props.valueField)"
      :menu-props="{ activator: 'parent' }"
      style="width: 2.2rem"
      density="comfortable"
      menu-icon="mdi-menu-down"
      class="currency-select"
      :class="{ 'currency-select--field': true }"
    >
      <template #selection>
        <div class="flag-wrapper">
          <CountryFlag :country="selectedCurrency?.isoCode" size="medium" />
        </div>
      </template>

      <template #item="{ props: innerProps, item }">
        <v-list-item
          v-bind="innerProps"
          :value="item"
          :data-value="item.value.currencyCode"
        >
          <template #title></template>
          <div class="d-flex align-center ga-4 flex-row justify-start">
            <CountryFlag :country="item.value.isoCode" size="small" />
            <p>
              {{ item.value.currencyCode }} -
              {{ item.value.currencyName }}
            </p>
          </div>
        </v-list-item>
      </template>
    </v-select>

    <Input
      ref="inputRef"
      :model-value="formattedValue ? String(formattedValue) : ''"
      :error="toValue(error)"
      :label="label"
      :placeholder="placeholder"
      :tooltip="tooltip"
      :description="error ? '' : description"
      :required="required"
      class="currency-input"
      @blur="(e: FocusEvent) => e.preventDefault()"
      @keydown="
        (e: KeyboardEvent) => {
          if (e.key === '-' && min === 0) e.preventDefault()
        }
      "
    />
  </div>
</template>

<style scoped>
:deep(.currency-select .v-field) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  border-right: 0px none !important;
  border: 1px solid #bdbdbd;
  max-height: 48px;
  min-width: 70px;
  padding: 8px;
}

:deep(.currency-input .v-field) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: 35px;
}

:deep(.currency-select .v-field__prepend-inner > .v-icon),
:deep(.currency-select .v-field__append-inner > .v-icon),
:deep(.currency-select .v-field__clearable > .v-icon) {
  display: flex !important;
  align-items: center !important;
  padding-bottom: 20px !important;
  padding-left: 8px !important;
  max-width: 6px !important;
  max-width: 3px !important;
}

:deep(.flag-wrapper) {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -32px;
  margin-left: 4px;
}
</style>
